import { useSyncExternalStore } from "react";

/**
 * Detect the host platform once and remember it. We base the verdict on
 * `navigator.platform` (deprecated but still the most reliable signal
 * for desktop OS), falling back to UA sniffing for Windows.
 *
 * Why this matters here:
 *   * Keyboard hint glyphs (⌘ vs Ctrl) need to match the user's
 *     muscle memory — wrong glyph is worse than no glyph.
 *   * Windows users right-click to paste; macOS users ⌘V. Some
 *     surfaces (e.g. address autocomplete) want different copy.
 */

export type OS = "mac" | "windows" | "linux" | "ios" | "android" | "other";

function detect(): OS {
  if (typeof navigator === "undefined") return "other";
  const platform = (navigator.platform || "").toLowerCase();
  const ua = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  if (platform.includes("mac") || /macintosh/.test(ua)) return "mac";
  if (platform.includes("win") || /windows/.test(ua)) return "windows";
  if (platform.includes("linux") || /linux/.test(ua)) return "linux";
  return "other";
}

const cached: OS = detect();

// useSyncExternalStore with constant snapshots = "render-once" hook.
const subscribe = () => () => undefined;

export function useOS(): OS {
  return useSyncExternalStore(
    subscribe,
    () => cached,
    () => "other",
  );
}

/** ⌘ on macOS, Ctrl elsewhere. */
export function modKey(os: OS): string {
  return os === "mac" || os === "ios" ? "⌘" : "Ctrl";
}

/** ⌥ on macOS, Alt elsewhere. */
export function altKey(os: OS): string {
  return os === "mac" || os === "ios" ? "⌥" : "Alt";
}

/** ⇧ on macOS, Shift elsewhere — Windows users tolerate the unicode glyph
 *  but plain "Shift" is friendlier. */
export function shiftKey(os: OS): string {
  return os === "mac" || os === "ios" ? "⇧" : "Shift";
}
